import { WelcomeContentDetailsEntity } from '../das-finsuite/admin-components/AdminModels';
import { FinanceCalendarEventEntity } from '../das-finsuite/widgets/finance-calendar-widget/FinanceCalendarData';
import { AnnouncementFlatTableEntity, ApplicationFlatTableEntity } from '../context/AppContextModels';

// --- Extensible search category system ---

export type SearchCategory = 'all' | 'applications' | 'announcements' | 'pages';

export interface SearchCategoryConfig {
  id: SearchCategory;
  label: string;
}

export const SEARCH_CATEGORIES: SearchCategoryConfig[] = [
  { id: 'all', label: 'All' },
  { id: 'applications', label: 'Applications' },
  { id: 'pages', label: 'Links' },
  { id: 'announcements', label: 'Product Launches' }
];

// --- Search result types ---

export interface SearchResultItem {
  id: string;
  category: SearchCategory;
  title: string;
  description?: string;
  isFavorite?: boolean;
  href?: string;
  meta?: string;
  external?: boolean;
}

export const MAX_RESULTS_PER_CATEGORY = 5;

// --- Quick Links (side nav + profile dropdown) ---

export interface QuickLinkItem {
  id: string;
  text: string;
  href: string;
  external?: boolean;
  section?: string;
}

// --- Search logic ---

const matchesQuery = (text: string | undefined | null, query: string): boolean => {
  if (!text) return false;
  return text.toLowerCase().includes(query);
};

export const searchApplications = (apps: ApplicationFlatTableEntity[], query: string, favoriteIds: string[]): SearchResultItem[] => {
  return apps
    .filter((app) => matchesQuery(app.applicationName, query) || matchesQuery(app.shortApplicationDescription, query))
    .map((app) => ({
      id: app.id,
      category: 'applications' as SearchCategory,
      title: app.applicationName,
      description: app.shortApplicationDescription,
      isFavorite: favoriteIds.includes(app.id),
      href: app.embeddingEnabled ? `/all-apps/${app.id}` : app.applicationLink || undefined
    }));
};

export const searchAnnouncements = (announcements: AnnouncementFlatTableEntity[], query: string): SearchResultItem[] => {
  return announcements
    .filter((a) => a.isActive && (matchesQuery(a.title, query) || matchesQuery(a.announcement, query) || matchesQuery(a.applicationName, query)))
    .map((a) => ({
      id: a.id,
      category: 'announcements' as SearchCategory,
      title: a.title,
      description: a.applicationName,
      meta: a.announcementDate,
      href: a.announcementLink || undefined
    }));
};

export const searchQuickLinks = (links: QuickLinkItem[], query: string): SearchResultItem[] => {
  return links
    .filter((link) => matchesQuery(link.text, query) || matchesQuery(link.section, query))
    .map((link) => ({
      id: link.id,
      category: 'pages' as SearchCategory,
      title: link.text,
      description: link.section,
      href: link.href,
      external: link.external
    }));
};

export const searchWelcomeContent = (items: WelcomeContentDetailsEntity[], query: string): SearchResultItem[] => {
  return items
    .filter((item) => item.displayText && (matchesQuery(item.displayText, query) || matchesQuery(item.description, query)))
    .map((item) => ({
      id: `welcome-${item.id || item.displayText}`,
      category: 'pages' as SearchCategory,
      title: item.displayText!,
      description: item.description || undefined,
      href: item.link || undefined,
      external: !!item.link
    }));
};

export const searchFinanceCalendar = (items: FinanceCalendarEventEntity[], query: string): SearchResultItem[] => {
  return items
    .filter((item) => item.event && (matchesQuery(item.event, query) || matchesQuery(item.description, query) || matchesQuery(item.scenario, query)))
    .map((item) => ({
      id: `calendar-${item.event}`,
      category: 'pages' as SearchCategory,
      title: item.event,
      description: item.description || undefined,
      meta: item.scenario
    }));
};

export interface GroupedSearchResults {
  applications: SearchResultItem[];
  announcements: SearchResultItem[];
  pages: SearchResultItem[];
}

export const performSearch = (
  query: string,
  apps: ApplicationFlatTableEntity[],
  announcements: AnnouncementFlatTableEntity[],
  favoriteIds: string[],
  quickLinks: QuickLinkItem[],
  welcomeContent: WelcomeContentDetailsEntity[],
  financeCalendar: FinanceCalendarEventEntity[]
): GroupedSearchResults => {
  const q = query.toLowerCase().trim();
  if (!q) return { applications: [], announcements: [], pages: [] };

  return {
    applications: searchApplications(apps, q, favoriteIds),
    announcements: searchAnnouncements(announcements, q),
    pages: [...searchQuickLinks(quickLinks, q), ...searchWelcomeContent(welcomeContent, q), ...searchFinanceCalendar(financeCalendar, q)]
  };
};
